// MacroHeaderPopup — kompakter Header-Button (Kalender-Icon + Count-Badge) der
// ein Popover mit den NÄCHST KOMMENDEN High-Impact-Releases öffnet.
// Ersetzt das raumgreifende „Heute"-Banner auf /markt (Doc 2026-06-16:
// „popup tab in den header"). Daten: /trading/macro/today (zeitlich rollierend).

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronRight, Sparkles } from 'lucide-react';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { fetchTodayReleases, type MacroRelease } from '@/lib/macro-api';
import { formatMacroTime } from '@/lib/macro-time';

const IMPACT_TONE: Record<MacroRelease['importance'], string> = {
  high: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
  medium: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  low: 'border-white/10 bg-white/[0.03] text-neutral-400',
};

function countryFlag(country: string): string {
  switch (country.toUpperCase()) {
    case 'US':
      return '🇺🇸';
    case 'DE':
      return '🇩🇪';
    case 'EU':
      return '🇪🇺';
    case 'GB':
      return '🇬🇧';
    case 'JP':
      return '🇯🇵';
    case 'AU':
      return '🇦🇺';
    case 'CA':
      return '🇨🇦';
    case 'CH':
      return '🇨🇭';
    case 'CN':
      return '🇨🇳';
    case 'NZ':
      return '🇳🇿';
    default:
      return '🌐';
  }
}

const LIMIT = 8;

export function MacroHeaderPopup() {
  const accessToken = useAuthStore(selectAccessToken);
  const [releases, setReleases] = useState<MacroRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    fetchTodayReleases(accessToken)
      .then((res) => {
        if (!cancelled) setReleases(res.releases ?? []);
      })
      .catch(() => {
        /* still — Header soll nie crashen */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Klick außerhalb + Escape schließt das Popover.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const high = releases.filter((r) => r.importance === 'high').slice(0, LIMIT);
  const count = high.length;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="glass relative grid size-7 place-items-center rounded-lg text-neutral-400 transition hover:text-white"
        title="Nächste Wirtschafts-Releases"
        aria-label="Nächste Wirtschafts-Releases"
      >
        <CalendarClock className="size-3.5" />
        {count > 0 ? (
          <span className="absolute -top-1 -right-1 grid min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-4 text-white">
            {count}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="glass-strong absolute right-0 top-full z-50 mt-2 w-80 rounded-2xl border border-amber-500/20 p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-amber-300">
              Nächste Releases
            </h3>
            <Link
              to="/trading/macro"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1 text-[11px] text-amber-300/80 hover:text-amber-200 hover:underline"
            >
              Alle ansehen
              <ChevronRight className="size-3" />
            </Link>
          </div>

          {loading ? (
            <div className="py-3 text-center text-[11px] text-neutral-500">Lade Kalender…</div>
          ) : count === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 px-3 py-3 text-center text-[11px] text-neutral-500">
              Keine kommenden High-Impact-Releases.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {high.map((r, i) => {
                const t = formatMacroTime(r.release_time_et, today);
                return (
                  <li
                    key={`${r.indicator}-${r.release_time_et}-${i}`}
                    className="flex items-center gap-2 text-[12px]"
                  >
                    <span className="shrink-0 text-sm" title={r.country}>
                      {countryFlag(r.country)}
                    </span>
                    <span className="w-12 shrink-0 tabular text-amber-400">{t.berlin}</span>
                    <span className="flex-1 truncate text-neutral-200" title={r.indicator}>
                      {r.indicator}
                    </span>
                    <span
                      className={`shrink-0 rounded border px-1 py-0.5 text-[9px] font-bold uppercase ${IMPACT_TONE[r.importance]}`}
                    >
                      HOCH
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-3 border-t border-white/5 pt-2">
            <Link
              to="/trading/macro"
              onClick={() => setOpen(false)}
              className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-amber-500/30 bg-amber-500/15 px-2 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/25"
            >
              <Sparkles className="size-3" />
              EVE-Macro-Tab
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
